import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch the user_profiles row and their organisation
  const fetchProfile = async (userId) => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*, organisations(*)')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
    return data;
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id);
        setProfile(p);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) {
          const p = await fetchProfile(session.user.id);
          setProfile(p);
        } else {
          setProfile(null);
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // ── Permission helpers ──────────────────────────────────────────────────────
  const role         = profile?.role          ?? null;
  const orgId        = profile?.org_id        ?? null;
  const organisation = profile?.organisations ?? null;
  const hasOrg       = orgId !== null;

  const can = {
    createInspection:  ['inspector', 'manager', 'admin'].includes(role),
    viewAllInspections: ['manager', 'reviewer', 'admin'].includes(role),
    editAllInspections: ['manager', 'admin'].includes(role),
    manageUsers:        role === 'admin',
    viewAuditLog:       ['manager', 'reviewer', 'admin'].includes(role),
  };

  // ── Auth actions ────────────────────────────────────────────────────────────
  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) throw error;
    return data;
  };

  const signInWithMicrosoft = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: { redirectTo: window.location.origin },
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  // Called on first login when user has no org yet
  const createOrganisation = async (name, slug) => {
    const { data, error } = await supabase
      .rpc('admin_create_organisation', { org_name: name, org_slug: slug });
    if (error) throw error;
    // Refresh profile to pick up new org_id and admin role
    if (user) {
      const p = await fetchProfile(user.id);
      setProfile(p);
    }
    return data;
  };

  const value = {
    user,
    profile,
    role,
    orgId,
    organisation,
    hasOrg,
    can,
    loading,
    signIn,
    signInWithGoogle,
    signInWithMicrosoft,
    signOut,
    createOrganisation,
    refreshProfile: async () => {
      if (user) {
        const p = await fetchProfile(user.id);
        setProfile(p);
      }
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
